const calculateBrightnessFromRGB = (r: number, g: number, b: number) => {
  return (r * 299 + g * 587 + b * 114) / 1000
}

/**
 * Thanks to https://gist.github.com/w3core/e3d9b5b6d69a3ba8671cc84714cca8a4
 * Calculate brightness value by RGB or HEX color.
 * @param color (String) The color value in RGB or HEX (for example: #000000 || #000 || rgb(0,0,0) || rgba(0,0,0,0))
 * @returns (Number) The brightness value (dark) 0 ... 255 (light)
 */
export const brightnessByColor = (color: string) => {
  const isHEX = color.indexOf('#') === 0
  const isRGB = color.indexOf('rgb') === 0
  if (isHEX) {
    const m = color
      .substr(1)
      .match(color.length === 7 ? /(\S{2})/g : /(\S{1})/g)
    if (m) {
      const r = parseInt(m[0], 16)
      const g = parseInt(m[1], 16)
      const b = parseInt(m[2], 16)
      return calculateBrightnessFromRGB(r, g, b)
    }
  }
  if (isRGB) {
    const m = color.match(/(\d+){3}/g)
    if (m) {
      const r = parseInt(m[0])
      const g = parseInt(m[1])
      const b = parseInt(m[2])
      return calculateBrightnessFromRGB(r, g, b)
    }
  }
  return null
}

/**
 * Return which theme of color you should apply to an element so it contrast
 * well with the color passed in parameter.
 *
 * default: use the color that you want (default one if there is one)
 * dark: use any color that is in darker shades
 * light: use any color that is in lighter shades
 *
 * @param color : color to analyze and get what type of color you should use
 *                for a text displayed on a background color for example
 * @return 'dark' | 'light' | 'default'
 */
export const getContrastedColorTheme = (
  color?: string
): 'dark' | 'light' | 'default' => {
  if (!color) {
    return 'default'
  }
  // Based on https://www.w3.org/TR/AERT/#color-contrast a good contrast difference is 125
  const brightness = brightnessByColor(color)
  return brightness && brightness > 125 ? 'dark' : 'light'
}

// Thanks to
// https://medium.com/@pppped/compute-an-arbitrary-color-for-user-avatar-starting-from-his-username-with-javascript-cd0675943b66
export const stringToHslColor = (str: string, s = 30, l = 80) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }

  const h = hash % 360
  return 'hsl(' + h + ', ' + s + '%, ' + l + '%)'
}

/**
 * Source: https://stackoverflow.com/questions/36721830/convert-hsl-to-rgb-and-hex
 */
export const hslToHex = (hsl: { h: number; s: number; l: number } | string) => {
  const parseHsl = (hsl: string): { h: number; s: number; l: number } => {
    const parts = hsl.replace('hsl(', '').replace(')', '').trim().split(',')
    return {
      h: parseInt(parts[0]),
      l: parseInt(parts[1]),
      s: parseInt(parts[2])
    }
  }

  const { h, l: originL, s } = typeof hsl === 'string' ? parseHsl(hsl) : hsl
  const l = originL / 100
  const a = (s * Math.min(l, 1 - l)) / 100
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0') // convert to Hex and prefix "0" if needed
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

const getContrastedTextColorFromRgbComponents = (
  rgb: number[],
  options: { darkColor?: string; lightColor?: string } = {}
) => {
  const threshold = 130 /* about half of 256. Lower threshold equals more dark text on dark background  */
  const cBrightness = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000

  const { darkColor, lightColor } = options
  return cBrightness > threshold
    ? darkColor || '#000000'
    : lightColor || '#ffffff'
}

/**
 * Gets the best color between white and black to contrast with another color
 * passed in param
 * Source: https://codepen.io/davidhalford/pen/AbKBNr
 */
export const getContrastedTextColor = (
  hex: string,
  options?: Parameters<typeof getContrastedTextColorFromRgbComponents>[1]
) => {
  /*
			From this W3C document: http://www.webmasterworld.com/r.cgi?f=88&d=9769&url=http://www.w3.org/TR/AERT#color-contrast
			
			Color brightness is determined by the following formula: 
			((Red value X 299) + (Green value X 587) + (Blue value X 114)) / 1000
      
      I know this could be more compact, but I think this is easier to read/explain.
			
			*/
  function cutHex(h: string) {
    return h.charAt(0) === '#' ? h.substring(1, 7) : h
  }
  function hexToR(h: string) {
    return parseInt(cutHex(h).substring(0, 2), 16)
  }
  function hexToG(h: string) {
    return parseInt(cutHex(h).substring(2, 4), 16)
  }
  function hexToB(h: string) {
    return parseInt(cutHex(h).substring(4, 6), 16)
  }

  const hRed = hexToR(hex)
  const hGreen = hexToG(hex)
  const hBlue = hexToB(hex)

  return getContrastedTextColorFromRgbComponents([hRed, hGreen, hBlue], options)
}

export const getContrastedTextColorFromRgb = (
  rgb: string | undefined,
  options?: Parameters<typeof getContrastedTextColorFromRgbComponents>[1]
) => {
  if (!rgb) {
    return '#ffffff'
  }
  const rgbComponents = rgb
    .substring(4, rgb.length - 1)
    .replace(/ /g, '')
    .split(',')
    .map(Number)
  return getContrastedTextColorFromRgbComponents(rgbComponents)
}
